import type { ClinicalDocumentData, DocumentPayload } from "@qendo/profile/files";
import type { DateArg } from "date-fns";
import type { DocumentPickerAsset } from "expo-document-picker";
import { useEffect, useId, useState } from "react";
import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { DocumentPicker } from "~/components/form/gallery/DocumentPicker";
import { TextArea } from "~/components/form/TextArea";
import { TextInput } from "~/components/form/TextInput";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { empty, filled } from "~/util";

export type DocumentFormProps = {
    document?: ClinicalDocumentData | null;
    painPlan?: boolean;
    onRequestClose: () => void;
} & Partial<FullPageModalProps>;

export const DocumentForm = ({ document, painPlan, onRequestClose, ...modalProps }: DocumentFormProps) => {
    const [name, setName] = useState<string>(document?.name ?? "");
    const [date, setDate] = useState<DateArg<Date> | null>(document?.date ?? null);
    const [pain, setPain] = useState<boolean>(painPlan ? true : (document?.pain_plan ?? false));
    const [description, setDescription] = useState<string>(document?.description ?? "");
    const [assets, setAssets] = useState<DocumentPickerAsset[]>([]);
    const [remove, setRemove] = useState<string[]>([]);
    const [dirty, setDirty] = useState(false);
    const { saving, setSaving, addToast } = useFeedback();
    const { storeDocument } = useGallery();
    const { can } = useProfile();
    const id = useId();

    const errorBag = useErrorBag<"name" | "files" | "remove" | "document_date" | "description">();
    const creating = empty(document?.id);
    const can_save =
        can("update", "documents") && !saving && filled(name) && filled(date) && (document?.id || filled(assets));

    useEffect(() => {
        setDirty(
            name !== (document?.name ?? "") ||
                date !== (document?.date ?? null) ||
                pain !== (painPlan ? true : (document?.pain_plan ?? false)) ||
                description !== (document?.description ?? "") ||
                filled(assets) ||
                filled(remove),
        );
    }, [document, name, date, pain, description, assets, remove, painPlan]);

    const doSave = () => {
        if (!can_save) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const document_payload = document?.id ? { id: document.id, remove } : {};

        const payload = {
            ...document_payload,
            name,
            document_date: date,
            pain_plan: painPlan ? true : pain,
            assets,
            description,
        } as DocumentPayload;

        storeDocument(payload)
            .then((id) => {
                if (id) {
                    addToast({
                        content: `${painPlan ? "Pain plan" : "Clinical document"} ${creating ? "uploaded" : "updated"}.`,
                    });

                    onRequestClose();
                } else {
                    errorBag.add("files", "Could not upload file");
                }
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;
    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            cancelable
            onRequestClose={onRequestClose}
            title={`${creating ? "Add" : "Update"} document`}
            dirty={dirty}
            headerRight={() => (
                <Button
                    disabled={!can_save}
                    variant="secondary"
                    size="sm"
                    title={creating ? "Upload" : "Save"}
                    onPress={doSave}
                />
            )}
        >
            <FieldGroup>
                <TextInput name="name" value={name} onChangeText={setName} label="Document name" />
                <DatePicker
                    name="document_date"
                    value={date}
                    onDateSelected={setDate}
                    label="Date listed on document"
                    disabled={is_saving}
                />
                <CheckboxInput
                    label="This is my prescribed pain plain"
                    value={painPlan ? true : pain}
                    onValueChange={setPain}
                    disabled={painPlan || is_saving}
                    icon="prescribed-file"
                />
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    name="description"
                    label="Desciption"
                    value={description}
                    onChangeText={setDescription}
                    disabled={is_saving}
                    optional
                />
                {errorBag.has("files") && <ErrorMessage>{errorBag.message("files")}</ErrorMessage>}
                {errorBag.has("remove") && <ErrorMessage>{errorBag.message("remove")}</ErrorMessage>}
                <DocumentPicker
                    value={document?.files}
                    remove={remove}
                    setRemove={setRemove}
                    assets={assets}
                    setAssets={setAssets}
                    disabled={is_saving}
                />
            </FieldGroup>
        </FullPageModal>
    );
};
